import { Check, Flame, Undo2 } from 'lucide-react';
import type { Habit } from '@/types';
import { Button } from '@/components/ui';
import { useHabitLog } from '@/features/habits/useHabitLog';
import { useHabitDetail } from '@/features/habits/useHabitDetail';
import { hapticNav } from '@/features/lifecycle/haptics';
import { habitColor } from '@/lib/habitColor';
import { HabitRing } from '@/components/habits/HabitRing';
import { NowProgressBar, NowStatusPill, NowTitle } from './NowShell';
import { formatTimeShort, progressPct } from './todaySelectors';

export interface NowHabitCardProps {
  habit: Habit;
  start: Date;
  end: Date;
  isDone: boolean;
  now: Date;
}

/**
 * Habit equivalent of NowCard. Shown when no task is in_progress / now-active
 * but a scheduled habit's window contains `now`. The explicit "Mark done" /
 * "Undo" action row mirrors the task card's one-tap actions (the ring toggle
 * alone read as a mystery on/off switch); tap on the body opens detail.
 */
export function NowHabitCard({ habit, start, end, isDone, now }: NowHabitCardProps) {
  const { toggleToday } = useHabitLog();
  const openDetail = useHabitDetail((s) => s.open);

  const pct = progressPct(start, end, now);
  const remainingMin = Math.max(0, Math.ceil((end.getTime() - now.getTime()) / 60_000));
  const streak = habit.currentStreak;

  return (
    <article className="overflow-hidden rounded-2xl border border-line-2 bg-card">
      <div className="flex items-stretch">
        <button
          type="button"
          aria-pressed={isDone}
          aria-label={`${habit.name} — ${isDone ? 'done today, tap to undo' : 'not yet done, tap to log'}`}
          onClick={() => toggleToday(habit)}
          className="pressable shrink-0 flex items-center justify-center p-4 transition-colors can-hover:hover:bg-rail focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
        >
          <HabitRing
            state={isDone ? 'done' : 'todo'}
            icon={habit.icon}
            size={48}
            color={habit.color}
          />
        </button>
        <button
          type="button"
          onClick={() => {
            void hapticNav();
            openDetail(habit.id);
          }}
          className="pressable min-w-0 flex-1 px-1 py-4 pe-4 text-left transition-colors can-hover:hover:bg-rail focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
        >
          <NowStatusPill className="mb-1">Now · habit</NowStatusPill>
          <NowTitle className="truncate">{habit.name}</NowTitle>
          <p className="mt-1 font-mono tabular-nums text-2xs text-t3">
            {formatTimeShort(start)} – {formatTimeShort(end)} · {remainingMin}m left
          </p>
          {isDone && (
            <p className="mt-1.5 inline-flex items-center gap-1 text-2xs font-medium text-ok-ink">
              <Check className="h-3.5 w-3.5" aria-hidden />
              Done today
            </p>
          )}
        </button>
      </div>
      <div className="flex items-center gap-3 px-4 pb-4 pt-1">
        <Button
          size="md"
          variant={isDone ? 'secondary' : 'primary'}
          className="flex-1"
          leadingIcon={
            isDone ? (
              <Undo2 className="h-4 w-4" aria-hidden />
            ) : (
              <Check className="h-4 w-4" aria-hidden />
            )
          }
          onClick={() => toggleToday(habit)}
        >
          {isDone ? 'Undo' : 'Mark done'}
        </Button>
        {streak > 0 && (
          <span className="inline-flex shrink-0 items-center gap-1 font-mono tabular-nums text-caption font-medium text-t2">
            <Flame className="h-3.5 w-3.5" aria-hidden />
            {streak}d streak
          </span>
        )}
      </div>
      <NowProgressBar
        pct={pct}
        className="h-1 w-full rounded-none"
        fillClassName="rounded-none"
        fillStyle={{ backgroundColor: habitColor(habit.color) }}
      />
    </article>
  );
}
